import { locationService } from '@/services/locationService';
import { ProjectLocation } from '@/types';
import { Loader2, MapPin, Navigation, Search } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface LocationInputProps {
  label: string;
  value: ProjectLocation;
  onChange: (location: ProjectLocation) => void;
  required?: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
}

interface PlaceSuggestion {
  description: string;
  placeId: string;
}

const LocationInput: React.FC<LocationInputProps> = ({
  label,
  value,
  onChange,
  required = false,
  error,
  placeholder = 'Search for an address...',
  className = '',
}) => {
  const [query, setQuery] = useState(value?.address || '');
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Update query when value changes from outside
  useEffect(() => {
    if (value?.address !== query) {
      setQuery(value?.address || '');
    }
  }, [value?.address]);

  // Handle place search with debouncing
  const searchPlaces = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (!locationService.isGoogleMapsAvailable()) {
      console.warn('Google Maps API not available');
      return;
    }

    setIsLoading(true);
    try {
      const predictions = await locationService.searchPlaces(searchQuery);
      const suggestionsList = predictions.map(prediction => ({
        description: prediction.description,
        placeId: prediction.place_id,
      }));

      setSuggestions(suggestionsList);
      setShowSuggestions(suggestionsList.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error searching places:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounced search
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuery = e.target.value;
      setQuery(newQuery);

      // Update the address field immediately for typing
      onChange({
        ...value,
        address: newQuery,
      });

      // Clear existing debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Debounce the search
      debounceRef.current = setTimeout(() => {
        searchPlaces(newQuery);
      }, 300);
    },
    [value, onChange, searchPlaces]
  );

  // Handle place selection
  const handlePlaceSelect = useCallback(
    async (placeId: string, description: string) => {
      setIsLoading(true);
      setShowSuggestions(false);
      setQuery(description);

      try {
        const placeDetails = await locationService.getPlaceDetails(placeId);

        if (placeDetails && placeDetails.geometry?.location) {
          // Parse address components
          const addressComponents = placeDetails.address_components || [];
          const getComponent = (types: string[]) => {
            const component = addressComponents.find(comp =>
              types.some(type => comp.types.includes(type))
            );
            return component?.long_name || '';
          };

          const newLocation: ProjectLocation = {
            address: description,
            city: getComponent(['locality', 'administrative_area_level_2']),
            state: getComponent(['administrative_area_level_1']),
            country: getComponent(['country']),
            zipCode: getComponent(['postal_code']),
            timezone: '', // We'll need to determine this separately or use a default
            coordinates: {
              lat: placeDetails.geometry.location.lat(),
              lng: placeDetails.geometry.location.lng(),
            },
          };

          onChange(newLocation);
        }
      } catch (error) {
        console.error('Error getting place details:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [onChange]
  );

  // Handle current location detection
  const handleCurrentLocation = useCallback(async () => {
    setIsGettingLocation(true);
    try {
      const coordinates = await locationService.getCurrentLocation();
      if (coordinates) {
        // Initialize Google Maps if needed
        await locationService
          .geocodeAddress({
            address: '',
            city: '',
            state: '',
            country: '',
          })
          .catch(() => {}); // This initializes the service

        // Reverse geocode to get address
        if (window.google?.maps) {
          const geocoder = new google.maps.Geocoder();
          geocoder.geocode({ location: coordinates }, (results, status) => {
            if (status === 'OK' && results?.[0]) {
              const result = results[0];
              const addressComponents = result.address_components || [];

              const getComponent = (types: string[]) => {
                const component = addressComponents.find(comp =>
                  types.some(type => comp.types.includes(type))
                );
                return component?.long_name || '';
              };

              const newLocation: ProjectLocation = {
                address: result.formatted_address,
                city: getComponent(['locality', 'administrative_area_level_2']),
                state: getComponent(['administrative_area_level_1']),
                country: getComponent(['country']),
                zipCode: getComponent(['postal_code']),
                timezone: '',
                coordinates,
              };

              setQuery(result.formatted_address);
              onChange(newLocation);
            } else {
              // Fallback: use coordinates only
              const newLocation: ProjectLocation = {
                address: `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`,
                city: '',
                state: '',
                country: '',
                zipCode: '',
                timezone: '',
                coordinates,
              };
              setQuery(newLocation.address);
              onChange(newLocation);
            }
          });
        } else {
          // Fallback when Google Maps is not available
          const newLocation: ProjectLocation = {
            address: `${coordinates.lat.toFixed(6)}, ${coordinates.lng.toFixed(6)}`,
            city: '',
            state: '',
            country: '',
            zipCode: '',
            timezone: '',
            coordinates,
          };
          setQuery(newLocation.address);
          onChange(newLocation);
        }
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    } finally {
      setIsGettingLocation(false);
    }
  }, [onChange]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showSuggestions || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
            const suggestion = suggestions[selectedIndex];
            handlePlaceSelect(suggestion.placeId, suggestion.description);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          setSelectedIndex(-1);
          break;
      }
    },
    [showSuggestions, suggestions, selectedIndex, handlePlaceSelect]
  );

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label */}
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Input Container */}
      <div className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            placeholder={placeholder}
            className={`block w-full px-3 py-2 pl-10 pr-20 text-sm border rounded-lg focus:ring-2 focus:ring-red-400 focus:border-red-400 transition-colors ${
              error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
            }`}
            autoComplete="off"
          />

          {/* Search Icon */}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isLoading ? (
              <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
            ) : (
              <Search className="h-4 w-4 text-gray-400" />
            )}
          </div>

          {/* Current Location Button */}
          {locationService.isGoogleMapsAvailable() && (
            <button
              type="button"
              onClick={handleCurrentLocation}
              disabled={isGettingLocation}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-red-500 transition-colors"
              title="Use current location"
            >
              {isGettingLocation ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Navigation className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.placeId}
                type="button"
                onClick={() => handlePlaceSelect(suggestion.placeId, suggestion.description)}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-red-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                  index === selectedIndex ? 'bg-red-50' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-900">{suggestion.description}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}

      {/* Location Details Display */}
      {value?.coordinates && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-800">Location Confirmed</span>
          </div>
          <div className="mt-1 text-xs text-green-700">
            <div>
              📍 {value.coordinates.lat.toFixed(6)}, {value.coordinates.lng.toFixed(6)}
            </div>
            {value.city && value.state && (
              <div>
                🏙️ {value.city}, {value.state} {value.zipCode}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Google Maps API Warning */}
      {!locationService.isGoogleMapsAvailable() && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
          <strong>Note:</strong> Google Maps integration is not configured. Address autocomplete is
          not available.
        </div>
      )}
    </div>
  );
};

export default LocationInput;
